import { useEffect, useState, type FormEvent } from 'react';
import {
  updateLocationCalendarPrefs,
  updateLocationTags,
} from '@/lib/scheduling';
import { useLocations } from '@/stores/locations';
import type { CalendarDensity, Location, TimeFormat } from '@/lib/types';

export interface LocationCalendarSettingsProps {
  location: Location;
}

/**
 * Per-pharmacy calendar settings panel. Displayed inside the LocationDetail
 * page so owners can:
 *   - Edit the day-annotation tag list (drives the calendar cell tag dropdown)
 *   - Pick a calendar density preset (standard / dense / spacious)
 *   - Pick a time format (am_pm / h24)
 */
export function LocationCalendarSettings({ location }: LocationCalendarSettingsProps) {
  const reloadLocations = useLocations((s) => s.load);

  const [tagsText, setTagsText] = useState('');
  const [density, setDensity] = useState<CalendarDensity>('standard');
  const [timeFormat, setTimeFormat] = useState<TimeFormat>('am_pm');
  const [savingTags, setSavingTags] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [tagsSavedAt, setTagsSavedAt] = useState<Date | null>(null);
  const [prefsSavedAt, setPrefsSavedAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setTagsText((location.day_annotation_tags ?? []).join('\n'));
    setDensity(location.calendar_density ?? 'standard');
    setTimeFormat(location.time_format ?? 'am_pm');
  }, [location]);

  async function onSaveTags(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSavingTags(true);
    try {
      const tags = tagsText.split('\n').map((t) => t.trim()).filter((t) => t.length > 0);
      await updateLocationTags(location.id, tags);
      await reloadLocations();
      setTagsSavedAt(new Date());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingTags(false);
    }
  }

  async function onSavePrefs(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSavingPrefs(true);
    try {
      await updateLocationCalendarPrefs(location.id, density, timeFormat);
      await reloadLocations();
      setPrefsSavedAt(new Date());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSavingPrefs(false);
    }
  }

  return (
    <div className="rounded-lg border border-border bg-card p-6 space-y-6">
      <h2 className="text-base font-medium">Calendar settings</h2>

      <form onSubmit={onSaveTags} className="space-y-2">
        <div>
          <label htmlFor="tags_text" className="block text-sm font-medium">Day annotation tags</label>
          <p className="text-xs text-muted-foreground">One per line. Tags appear in the calendar cell dropdown to flag days that need attention.</p>
        </div>
        <textarea
          id="tags_text"
          value={tagsText}
          onChange={(e) => setTagsText(e.target.value)}
          rows={Math.max(7, tagsText.split('\n').length + 1)}
          placeholder={'Vaccine clinic\nInventory clean\nTraining\n...'}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
        />
        <div className="flex items-center gap-2">
          <button type="submit" disabled={savingTags} className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50">{savingTags ? 'Saving…' : 'Save tags'}</button>
          {tagsSavedAt && <p className="text-xs text-muted-foreground">Saved at {tagsSavedAt.toLocaleTimeString()}.</p>}
        </div>
      </form>

      <hr className="border-border" />

      <form onSubmit={onSavePrefs} className="space-y-3">
        <h3 className="text-sm font-medium">Calendar preferences</h3>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <label htmlFor="density" className="block text-sm font-medium">Density</label>
            <select id="density" value={density} onChange={(e) => setDensity(e.target.value as CalendarDensity)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="standard">Standard · 4 chips per AM/PM half</option>
              <option value="dense">Dense · more chips, smaller (coming v4)</option>
              <option value="spacious">Spacious · fewer, larger (coming v4)</option>
            </select>
            <p className="text-[11px] text-muted-foreground">Currently only Standard ships visually. Dense/Spacious are saved but not yet rendered differently.</p>
          </div>

          <div className="space-y-1">
            <label htmlFor="time_format" className="block text-sm font-medium">Time format</label>
            <select id="time_format" value={timeFormat} onChange={(e) => setTimeFormat(e.target.value as TimeFormat)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="am_pm">am/pm · &quot;9a-1p&quot;</option>
              <option value="h24">24h · &quot;9-13&quot;</option>
            </select>
            <p className="text-[11px] text-muted-foreground">Affects chip labels and modal time displays.</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button type="submit" disabled={savingPrefs} className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50">{savingPrefs ? 'Saving…' : 'Save preferences'}</button>
          {prefsSavedAt && <p className="text-xs text-muted-foreground">Saved at {prefsSavedAt.toLocaleTimeString()}.</p>}
        </div>
      </form>

      {error && <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">{error}</p>}
    </div>
  );
}
